"use client";

import React, { useState } from 'react';
import type { LocationData } from '@/types/locations';
import { formatCurrency } from '@/lib/locations';

interface RetirementCalculatorProps {
  location: LocationData;
}

export default function RetirementCalculator({ location }: RetirementCalculatorProps) {
  const [annualCosts, setAnnualCosts] = useState(location.annualLivingCosts);
  const [swr, setSwr] = useState(4);

  const statePension = location.statePension;
  const gap = annualCosts - statePension;
  const multiplier = 100 / swr;
  const retirementNumber = gap > 0 ? gap * multiplier : 0;
  const costsCovered = gap <= 0;

  return (
    <div>
      <h2 className="text-2xl font-bold text-investments-700 mb-6">
        Your {location.name} Retirement Number
      </h2>
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="bg-cashflow-50 p-6 border-2 border-ink text-center">
          <label htmlFor="field-retirement-1" className="text-sm text-cashflow-600 font-medium mb-2 block">Annual Living Costs</label>
          <div className="flex items-center justify-center gap-1">
            <span className="text-cashflow-600 text-3xl">£</span>
            <input
              id="field-retirement-1" type="number"
              value={annualCosts}
              onChange={(e) => setAnnualCosts(Number(e.target.value))}
              className="w-36 text-3xl font-bold text-cashflow-700 text-center border-2 border-cashflow-300 rounded px-2 py-1"
            />
          </div>
          <p className="text-xs text-cashflow-500 mt-2">per year</p>
        </div>
<div className="bg-positive-50 p-6 border-2 border-ink text-center">
          <div className="mb-4">
            <div className="text-sm text-positive-600 font-medium mb-1 block">State Pension</div>
            <p className="text-2xl font-bold text-positive-700">
              {formatCurrency(statePension)}
            </p>
            <p className="text-xs text-positive-500">£12,548/yr at age 67</p>
          </div>
          <div className="border-t-2 border-positive-500 pt-4">
            <div className="text-sm text-positive-600 font-medium mb-1 block">Gap to Fund</div>
            {costsCovered ? (
              <p className="text-lg font-bold text-positive-700">Your costs are covered</p>
            ) : (
              <>
                <p className="text-2xl font-bold text-positive-700">
                  {formatCurrency(gap)}
                </p>
                <p className="text-xs text-positive-500">Annual costs − State Pension</p>
              </>
            )}
          </div>
        </div>
        <div className="bg-investments-50 p-6 border-2 border-ink text-center">
          <div className="text-sm text-investments-600 font-medium mb-2 block">Retirement Number</div>
          {costsCovered ? (
            <p className="text-lg font-bold text-investments-700">Your costs are covered by the State Pension</p>
          ) : (
            <>
              <p className="text-4xl font-black text-investments-700">
                {formatCurrency(retirementNumber)}
              </p>
              <p className="text-sm text-investments-600 mt-2 font-medium">{Math.round(multiplier)}× gap ({swr}% rule)</p>
              <p className="text-xs text-investments-500 mt-1">This does not include your primary residence</p>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center gap-8 mb-6">
        <div className="flex items-center gap-2">
          <label htmlFor="field-retirement-2" className="text-sm text-gray-600">Safe Withdrawal Rate:</label>
          <input
            id="field-retirement-2" type="number"
            value={swr}
            onChange={(e) => setSwr(Number(e.target.value))}
            step="0.1"
            min="1"
            max="10"
            className="w-16 text-sm font-medium text-center border-2 border-gray-300 rounded px-2 py-1"
          />
          <span className="text-sm text-gray-600">%</span>
        </div>
        <div className="text-xs text-gray-600">
          <span className="inline-block w-3 h-3 bg-positive-50 border-2 border-ink mr-1"></span>
          <span className="text-positive-600 font-medium">4%</span> = worst case
          <span className="inline-block w-3 h-3 bg-cashflow-50 border-2 border-ink ml-4 mr-1"></span>
          <span className="text-cashflow-600 font-medium">5%</span> = optimised
          <span className="inline-block w-3 h-3 bg-investments-50 border-2 border-ink ml-4 mr-1"></span>
          <span className="text-investments-600 font-medium">6%</span> = more aggressive
        </div>
      </div>
      <p className="text-sm text-gray-500 text-center mt-4">
        Based on {swr}% withdrawal rate · Assuming retirement at state pension age · {location.name} cost of living data · UK State Pension 2026/27
      </p>
    </div>
  );
}